import { useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";
import { getWorkById } from "@/lib/works";
import { TYPE_LABELS } from "@/types/work";

interface FavoritesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function FavoritesDrawer({ open, onClose }: FavoritesDrawerProps) {
  const navigate = useNavigate();
  const { favorites, removeFavorite } = useFavorites();
  const works = favorites
    .map((id) => getWorkById(id))
    .filter((work): work is NonNullable<typeof work> => Boolean(work));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <button
        type="button"
        aria-label="关闭收藏夹"
        className="absolute inset-0 h-full w-full bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col animate-slide-down border-l border-background-200 bg-background-50">
        <header className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
            <i className="ri-heart-3-fill text-primary-500"></i>
            我的收藏
            <span className="text-xs font-normal text-foreground-500">
              ({works.length})
            </span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {works.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
                <i className="ri-heart-3-line"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">还没有收藏任何作品</p>
              <p className="mt-1 text-xs text-foreground-400">
                在影视详情页点击收藏即可加入这里
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {works.map((work) => (
                <li
                  key={work.id}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100/60 p-2.5"
                >
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`/detail/${work.id}`);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left cursor-pointer"
                  >
                    <span className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={work.cover}
                        alt={work.title}
                        title={`${work.title} 剧情介绍`}
                        className="h-full w-full object-top"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-foreground-950">
                        {work.title}
                      </span>
                      <span className="mt-0.5 block text-xs text-foreground-500">
                        {TYPE_LABELS[work.type]} · {work.year}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFavorite(work.id)}
                    aria-label="取消收藏"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}